import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, Lock, Mail } from "lucide-react";
import { Logo } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("rk_admin_token", data.token);
      localStorage.setItem("rk_admin_user", JSON.stringify(data.user));
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "h-12 w-full rounded-lg border border-line bg-warm/60 pl-11 pr-3.5 text-[14px] font-medium text-ink placeholder:text-ink-soft/60 focus:outline-none focus:ring-1 focus:ring-gold focus:border-gold transition";

  return (
    <div className="min-h-screen bg-navy flex items-center justify-center px-4" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex justify-center mb-8" data-testid="admin-login-logo">
          <Logo dark />
        </Link>
        <form onSubmit={submit} className="bg-white rounded-xl p-8 sm:p-10 shadow-[0_30px_80px_rgba(0,0,0,0.35)]" data-testid="admin-login-form">
          <h1 className="font-display text-2xl font-semibold text-navy">Admin Login</h1>
          <p className="mt-1.5 text-sm text-ink-soft">Sign in to view and manage property enquiries.</p>

          {error && (
            <p className="mt-5 text-[13px] font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3" data-testid="admin-login-error">
              {error}
            </p>
          )}

          <div className="mt-7 space-y-5">
            <div className="relative">
              <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-soft" />
              <input type="email" required placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)}
                className={inputCls} data-testid="admin-login-email" />
            </div>
            <div className="relative">
              <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-soft" />
              <input type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)}
                className={inputCls} data-testid="admin-login-password" />
            </div>
          </div>

          <button type="submit" disabled={loading}
            className="mt-7 w-full bg-gold text-navy hover:bg-gold-dark disabled:opacity-60 transition-colors rounded-lg px-6 py-3.5 text-[15px] font-semibold flex items-center justify-center gap-2"
            data-testid="admin-login-submit">
            {loading ? <Loader2 size={17} className="animate-spin" /> : "Sign In"}
          </button>
        </form>
        <p className="mt-6 text-center">
          <Link to="/" className="text-[13px] text-white/50 hover:text-gold transition-colors" data-testid="admin-login-back-home">
            ← Back to website
          </Link>
        </p>
      </div>
    </div>
  );
}
